import { useCallback, useEffect, useState } from 'react';
import type { TrackPermissionRow, TrackRole } from '@verybigthings/trackframe-shared/schemas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { hub } from '@/lib/hub-client';
import { RoleFormDialog, roleWriteBody } from './RoleFormDialog';
import { RolesMatrix } from './RolesMatrix';

/** The role with `names` all ticked or all unticked — the FULL list the PATCH carries (D4). */
export function withPermissions(role: TrackRole, names: readonly string[], checked: boolean): string[] {
  const without = role.permissions.filter((p) => !names.includes(p));
  return checked ? [...without, ...names] : without;
}

/**
 * Roles tab of the track page (HUB-109 D4 / TAZ-07). Loads roles + the
 * permission catalog in one call; a tick is an optimistic full-replacement
 * PATCH that reverts on failure.
 */
export function RolesTab({ trackId }: { trackId: string }) {
  const [roles, setRoles] = useState<TrackRole[]>([]);
  const [permissions, setPermissions] = useState<TrackPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ open: boolean; role: TrackRole | null }>({ open: false, role: null });
  const [pendingDelete, setPendingDelete] = useState<TrackRole | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await hub.tracksRolesList({ params: { id: trackId } });
      setRoles(res.roles);
      setPermissions(res.permissions);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [trackId]);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceRole = (next: TrackRole) =>
    setRoles((prev) => prev.map((r) => (r.id === next.id ? next : r)));

  const onSetPermissions = async (role: TrackRole, names: string[], checked: boolean) => {
    const next = { ...role, permissions: withPermissions(role, names, checked) };
    replaceRole(next);
    setBusy(true);
    setError(null);
    try {
      const saved = await hub.tracksRoleUpdate({
        params: { id: trackId, roleId: role.id },
        body: roleWriteBody(next),
      });
      replaceRole(saved);
    } catch (err) {
      replaceRole(role);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onSaved = (saved: TrackRole) => {
    setRoles((prev) => (prev.some((r) => r.id === saved.id) ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved]));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    setError(null);
    try {
      await hub.tracksRoleDelete({ params: { id: trackId, roleId: pendingDelete.id } });
      setRoles((prev) => prev.filter((r) => r.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Roles × permissions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading roles…</p>
          ) : (
            <RolesMatrix
              roles={roles}
              permissions={permissions}
              busy={busy}
              error={error}
              onSetPermissions={(role, names, checked) => void onSetPermissions(role, names, checked)}
              onAddRole={() => setForm({ open: true, role: null })}
              onEditRole={(role) => setForm({ open: true, role })}
              onDeleteRole={setPendingDelete}
            />
          )}
        </CardContent>
      </Card>

      <RoleFormDialog
        open={form.open}
        trackId={trackId}
        role={form.role}
        permissions={permissions}
        onClose={() => setForm({ open: false, role: null })}
        onSaved={onSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete role?"
        description="Users lose everything this role granted. Their level, if any, stays."
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
